/* global BigInt */
import { EnergySync, commitSequence } from './energySync';
import { calculateSnapshot } from '../calculations/powerBalanceDelegate';
import { retainCoherentBalance } from '../calculations/alignmentDelegate';
import { withLineage, CALCULATION_VERSION } from '../calculations/lineage';
import { calculateCosts, calculateLeaseEconomics } from '../calculations/costCalculationDelegate';
import { buildEnergyViewModel } from '../../utils/energyViewModel';

const envelope = (data, metadata = {}) => ({ status: 'success', data, metadata });
const identity = row => `${row.messageType}:${row.deviceId}`;

// A separate, bounded current-state model. Historical accounting never runs on
// this path, and its cursor cannot confirm coverage of a historical dataset.
export class LiveEnergyCalculator {
  async call(method, ...args) { return this[method](...args); }
  close() {}

  bootstrap(data, scope, now) {
    if (this.scope?.customerId !== scope.customerId || this.scope?.siteId !== scope.siteId
      || JSON.stringify(this.configuration) !== JSON.stringify(data.siteConfiguration)) this.lastCoherent = null;
    this.scope = scope;
    this.configuration = data.siteConfiguration;
    this.costInputs = data.costInputs;
    this.rows = new Map((data.latest?.rows || []).map(row => [identity(row), row]));
    this.candidates = data.latest?.candidates || [];
    this.confirmedCommitSeq = data.watermarkCommitSeq;
    this.bootstrapBoundary = data.asOf || null;
    return this.calculate(now);
  }

  replay(events, throughCommitSeq, _range, now) {
    this.bootstrapBoundary = null;
    const updates = [];
    let sourceClockRewound = false;
    for (const event of events) {
      if (event.eventKind !== 'current_updated') continue;
      if (!event.payload) throw new Error('REBOOTSTRAP_REQUIRED');
      const row = { ...event, messageId: event.payload.messageId };
      const old = this.rows.get(identity(row));
      if (old && Date.parse(row.observedAt) < Date.parse(old.observedAt)) sourceClockRewound = true;
      // DBAM's current_updated commit is authoritative publication order. The
      // source timestamp positions history; it does not decide current state.
      this.rows.set(identity(row), row);
      updates.push(row);
    }
    if (sourceClockRewound) {
      this.candidates = updates;
    } else this.candidates.push(...updates);
    this.confirmedCommitSeq = throughCommitSeq;
    return this.calculate(now);
  }

  calculate(now) {
    const rows = [...this.rows.values()];
    const newest = Math.max(...rows.map(row => Date.parse(row.observedAt)).filter(Number.isFinite));
    this.candidates = this.candidates.filter(row => Date.parse(row.observedAt) >= newest - 60000).slice(-1000);
    const candidate = calculateSnapshot(this.candidates.concat(rows), this.bootstrapBoundary, this.configuration);
    // A bootstrap can land between members of a telemetry batch. Recover the
    // newest complete boundary in its candidates without clearing a good balance.
    if (!this.lastCoherent && candidate.balance.status !== 'complete') {
      for (let seconds = 10; seconds <= 60; seconds += 10) {
        const prior = calculateSnapshot(this.candidates.concat(rows), new Date(Date.parse(candidate.boundary) - seconds * 1000).toISOString(), this.configuration);
        if (prior.balance.status === 'complete') { this.lastCoherent = prior; break; }
      }
    }
    const retained = retainCoherentBalance(candidate, this.lastCoherent, now);
    if (retained.snapshot.balance.status === 'complete') this.lastCoherent = retained.snapshot;
    const snapshot = retained.snapshot;
    const context = { confirmedCommitSeq: this.confirmedCommitSeq, configurationVersion: this.configuration?.configurationVersion,
      cutoffTime: snapshot.boundary, sourceWatermark: { inputs: snapshot.aligned.inputs } };
    const latest = envelope(rows, { asOf: candidate.boundary, calculationVersion: CALCULATION_VERSION });
    const model = buildEnergyViewModel({ latest, canopyInventory: envelope(this.scope.canopyInventory || []) });
    const values = role => snapshot.aligned.inputs[role] && !snapshot.aligned.inputs[role].stale ? snapshot.aligned.inputs[role].values : null;
    const coherent = snapshot.balance.status === 'complete';
    model.power = {
      solarKw: coherent ? values('site')?.generationKw ?? null : null,
      bessKw: coherent ? values('bess')?.netPowerKw ?? values('bess')?.actualPowerKw ?? null : null,
      loadKw: coherent ? values('load')?.servedPowerKw ?? null : null,
      gridKw: coherent ? values('pcc')?.powerKw ?? null : null,
      residualKw: snapshot.balance.residualKw ?? null, toleranceKw: snapshot.balance.toleranceKw ?? null,
      balanced: snapshot.balance.balanceWithinTolerance ?? null,
    };
    const current = role => candidate.aligned.inputs[role] && !candidate.aligned.inputs[role].stale
      && !candidate.aligned.reasonCodes.includes(`ambiguousSource:${role}`) ? candidate.aligned.inputs[role].values : null;
    model.load.powerKw = current('load')?.servedPowerKw ?? null;
    model.load.presentDemandKw = model.load.powerKw;
    model.load.feeders = candidate.feeders;
    model.pcc.powerKw = current('pcc')?.powerKw ?? null;
    const pcc = values('pcc') || current('pcc');
    model.pcc.frequencyHz = pcc?.siteSide?.frequencyHz ?? pcc?.utilitySide?.frequencyHz ?? null;
    model.pcc.electrical = candidate.electrical;
    const battery = current('bess');
    model.bess.netKw = battery?.netPowerKw ?? battery?.actualPowerKw ?? null;
    model.bess.actualKw = model.bess.netKw;
    model.bess.pcsKw = battery?.pcs?.actualPowerKw ?? battery?.pcs?.powerKw ?? battery?.actualPowerKw ?? null;
    model.bess.availableChargeKw = battery?.limits?.availableChargeKw ?? null;
    model.bess.availableDischargeKw = battery?.limits?.availableDischargeKw ?? null;
    model.bess.storedKwh = candidate.bessEnergy?.storedEnergyUsedKwh ?? candidate.bessEnergy?.reportedStoredEnergyKwh ?? null;
    model.bess.usableCapacityKwh = snapshot.bessEnergy?.operatingWindowCapacityKwh ?? null;
    model.bess.usableKwh = candidate.bessEnergy?.usableEnergyAboveReserveKwh ?? null;
    model.bess.chargeHeadroomKwh = candidate.bessEnergy?.chargeHeadroomKwh ?? null;
    model.bess.estimatedDeliverableKwh = candidate.bessEnergy?.estimatedDeliverableAcEnergyKwh ?? null;
    model.ems.actualKw = model.bess.pcsKw;
    const emsRows = rows.filter(row => row.messageType === 'ems.telemetry' && row.payload?.ems?.dataStatus !== 'unavailable');
    const ems = emsRows.length === 1 && Date.parse(emsRows[0].payload.timestamp) <= Date.parse(candidate.boundary)
      && Date.parse(candidate.boundary) - Date.parse(emsRows[0].payload.timestamp) <= 10000 ? emsRows[0].payload.ems : null;
    model.bess.requestedKw = ems?.dispatch?.requestedKw ?? null;
    model.bess.constrainedKw = ems?.dispatch?.constrainedKw ?? null;
    const costResult = calculateCosts(this.costInputs, candidate, [], { start: candidate.boundary, end: candidate.boundary },
      { siteId: this.scope.siteId, financialAsOf: new Date(now).toISOString() }, context);
    const costs = costResult.current;
    model.costs.currency = costs.currency || this.costInputs?.demoLeaseProposal?.currency || null;
    model.costs.importEnergyPricePerKwh = costs.importEnergyPricePerKwh?.value ?? null;
    model.costs.exportCreditPerKwh = costs.exportCreditPerKwh?.value ?? null;
    model.costs.utilityRatePerHour = costs.utilityEnergyCostRate?.value ?? null;
    model.costs.modeledGenerationRatePerHour = costs.modeledGenerationCostRate?.value ?? null;
    model.costs.allocatedLeaseRatePerHour = costs.allocatedLeaseCostRate?.value ?? null;
    model.costs.modeledLcoePerKwh = costResult.summary.modeledLcoe?.value ?? null;
    const fleets = new Set(rows.filter(row => row.fleetId).map(row => row.fleetId));
    model.leaseEconomics = calculateLeaseEconomics(this.costInputs, candidate, null,
      { ...this.scope, soleFleetAtSite: fleets.size === 1 && fleets.has(this.scope.fleetId) }, new Date(now).toISOString(), context);
    model.meta = { ...model.meta, freshness: retained.freshness, balanceRetained: retained.retained,
      balanceCutoffTime: snapshot.boundary, watermarkCommitSeq: this.confirmedCommitSeq,
      timezone: this.configuration?.timezone || 'UTC', calculationVersion: CALCULATION_VERSION };
    model.measures = { costs: { current: costs, summary: { modeledLcoe: costResult.summary.modeledLcoe } },
      bess: Object.fromEntries(['storedKwh', 'usableKwh', 'usableCapacityKwh', 'chargeHeadroomKwh', 'estimatedDeliverableKwh'].map(key => [key,
        withLineage({ value: model.bess[key], unit: 'kWh', reasonCodes: candidate.bessEnergy?.reasonCodes || [] },
          { ...context, cutoffTime: candidate.boundary, sourceWatermark: { inputs: candidate.aligned.inputs } })])),
      power: Object.fromEntries(Object.entries(model.power).filter(([key]) => key !== 'balanced').map(([key, value]) => [key,
      withLineage({ value, unit: 'kW', status: coherent ? 'complete' : 'unavailable', reasonCodes: snapshot.balance.reasonCodes || [] }, context)])) };
    this.result = { raw: { latest }, model };
    return this.result;
  }

  clock(_range, now) {
    return this.calculate(now);
  }
}

export class LiveEnergySync extends EnergySync {
  constructor(options) { super({ ...options, calculator: new LiveEnergyCalculator() }); }
  async bootstrap() {
    const data = await this.bootstrapData();
    if (this.closed) return;
    commitSequence(data.watermarkCommitSeq);
    if (data.customerId !== this.scope.customerId || data.siteId !== this.scope.siteId) throw new Error('BOOTSTRAP_SCOPE_MISMATCH');
    const result = await this.calculator.call('bootstrap', data, this.scope, this.now());
    if (this.closed) return;
    this.confirmedCommitSeq = data.watermarkCommitSeq;
    if (commitSequence(this.highestReceivedCommitSeq) < commitSequence(this.confirmedCommitSeq)) this.highestReceivedCommitSeq = this.confirmedCommitSeq;
    this.frames.forEach((_frame, seq) => { if (commitSequence(seq) <= commitSequence(this.confirmedCommitSeq)) this.frames.delete(seq); });
    this.onUpdate(result, this.cursors());
  }
}

export function mergeLiveEnergy(calculated, live) {
  if (!live) return calculated;
  const model = calculated.model;
  const current = live.model;
  return { ...calculated, raw: { ...calculated.raw, latest: live.raw.latest }, model: {
    ...model, meta: { ...model.meta, ...current.meta, coveragePct: model.meta.coveragePct }, power: current.power,
    canopies: current.canopies, ems: current.ems,
    bess: { ...model.bess, ...current.bess, equipment: model.bess.equipment },
    load: { ...model.load, powerKw: current.load.powerKw, presentDemandKw: current.load.presentDemandKw, feeders: current.load.feeders },
    pcc: { ...model.pcc, powerKw: current.pcc.powerKw, frequencyHz: current.pcc.frequencyHz, electrical: current.pcc.electrical },
    leaseEconomics: { ...model.leaseEconomics, ...current.leaseEconomics,
      averageGenerationCostThisMonthPerKwh: model.leaseEconomics?.averageGenerationCostThisMonthPerKwh ?? null,
      coverage: model.leaseEconomics?.coverage },
    costs: { ...model.costs, currency: current.costs.currency, utilityRatePerHour: current.costs.utilityRatePerHour,
      importEnergyPricePerKwh: current.costs.importEnergyPricePerKwh, exportCreditPerKwh: current.costs.exportCreditPerKwh,
      modeledGenerationRatePerHour: current.costs.modeledGenerationRatePerHour, allocatedLeaseRatePerHour: current.costs.allocatedLeaseRatePerHour,
      modeledLcoePerKwh: current.costs.modeledLcoePerKwh },
    measures: { ...model.measures, power: current.measures.power,
      bess: { ...model.measures?.bess, ...current.measures.bess },
      costs: { ...model.measures?.costs, current: current.measures.costs.current,
        summary: { ...model.measures?.costs?.summary, ...current.measures.costs.summary } } },
  } };
}
